/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useContext, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import { dataFetcher, endpoints, axios, baseUrl } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { AuthContext, AppContext } from 'Context';
import { v4 as uuid } from 'uuid';
import useSWR from 'swr';
import screenfull from 'screenfull';
import PermissionDeniedModal from 'Components/PermissionDeniedModal';
import PlayerControls from './PLayerControls';
import HlsPlayer from './HlsPlayer';
import NativeVideoPlayer from './NativeVideoPlayer';
import { probeVideo } from './lib/probeVideo';
import { prefetch } from './lib/prefetch';
import { classifyFormat } from './lib/formats';
import styles from './index.module.css';

const useStyles = makeStyles({
  playerWrapper: {
    width: '100%',
    position: 'relative',
    height: '100vh',
    background: 'black',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& video': {
      objectFit: 'contain',
    },
  },
});

const format = (seconds) => {
  if (Number.isNaN(seconds)) {
    return '00:00';
  }
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, '0');
  if (hh) {
    return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
  }
  return `${mm}:${ss}`;
};
const count = 0;

// ===== TEST FLAG: FORCE_RAW_FILE =====
// When true, every video plays from video_raw_file directly, bypassing the
// normal transcoded → video_file → mp4 → raw priority. Used to benchmark
// the new NativeVideoPlayer pipeline on the raw upload (the file this whole
// upgrade was built for).
// TO SHIP: comment out this block AND the matching block inside onSuccess
// (search "TEST FLAG: FORCE_RAW_FILE"). Don't delete — we may want to
// re-enable it later for regression testing.
// const FORCE_RAW_FILE = true;
// ===== END TEST FLAG =====

export function VideoPlayer() {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  // Auto-hide timer for the controls overlay. MUST be a ref — a plain local
  // is recreated as undefined every render, so clearTimeout(timeout) silently
  // no-ops and stale hide-timers leak. A leaked timer firing after the video
  // completes is exactly what made the controls + next-video layout vanish.
  const timeoutRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const { setAppSnackbar, subscriptionDetails, userDetails } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const { videoID, type } = useParams();
  const [loading, setLoading] = useState(true);
  const [videoDetails, setVideoDetails] = useState({});
  const playerRef = useRef(null);
  const [vttContent, setVttContent] = useState(undefined);
  const [spriteImageRow, setSpriteImageRow] = useState(undefined);
  const [spriteImageColumn, setSpriteImageColumn] = useState(undefined);
  const [spriteImageUrl, setSpriteImageUrl] = useState(undefined);
  const [screenSize, setScreenSize] = useState(false);
  const [showPlayAgainButton, setPlayAgainButton] = useState(false);
  const [showControllers, setControllers] = useState(true);
  const [hoverValue, setHoverValue] = useState(null);
  const [nextVideoDetails, setNextVideoDetails] = useState(undefined);
  const [countDown, setCountDown] = useState(10);

  // Detect orientation from raw video file dimensions (same as Flutter)
  const [detectedOrientation, setDetectedOrientation] = useState(null);
  useEffect(() => {
    const rawUrl = videoDetails?.video_raw_file;
    if (!rawUrl) return;

    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.muted = true;
    tempVideo.playsInline = true;

    const cleanup = () => {
      tempVideo.removeAttribute('src');
      tempVideo.load();
    };

    tempVideo.addEventListener('loadedmetadata', () => {
      const w = tempVideo.videoWidth;
      const h = tempVideo.videoHeight;
      setDetectedOrientation(w > h ? '1' : '2');
      cleanup();
    });

    tempVideo.addEventListener('error', () => {
      cleanup();
    });

    tempVideo.src = rawUrl;

    return cleanup;
  }, [videoDetails?.video_raw_file]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
  const isPortraitVideo = detectedOrientation === '2';
  const videoObjectFit = (isMobile && isPortraitVideo) ? 'cover' : 'contain';

  // Video URL fallback logic
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const loadTimeoutRef = useRef(null);

  // Probe + warmup orchestration. Runs whenever currentVideoUrl changes
  // (and it's not HLS — HlsPlayer owns its own pipeline). The probe tells
  // us size / Accept-Ranges / faststart so NativeVideoPlayer can pick a
  // sane preload strategy; the prefetch warms the browser HTTP cache so
  // the visible <video>'s first range requests come back instantly.
  const [probe, setProbe] = useState(null);
  const probeAbortRef = useRef(null);
  const prefetchAbortRef = useRef(null);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);

  useEffect(() => {
    if (!currentVideoUrl) return undefined;
    if (currentVideoUrl.includes('.m3u8')) return undefined;

    if (probeAbortRef.current) probeAbortRef.current.abort();
    if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
    setProbe(null);
    setHasStartedPlayback(false);

    const ctrl = new AbortController();
    probeAbortRef.current = ctrl;
    (async () => {
      try {
        const p = await probeVideo(currentVideoUrl, ctrl.signal);
        setProbe(p);
        if (p.available && p.acceptRanges) {
          const pf = new AbortController();
          prefetchAbortRef.current = pf;
          // Fire-and-forget — aborts on next URL change or first playback.
          prefetch(currentVideoUrl, p, pf.signal);
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          setProbe({ available: false, error: e.message });
        }
      }
    })();

    return () => {
      ctrl.abort();
      if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
    };
  }, [currentVideoUrl]);

  const [hasPermission, setHasPermission] = useState(true);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Check if user has permission to view this video
  // TODO: TEMPORARY - All access enabled. Revert to original logic when needed.
  useEffect(() => {
    setHasPermission(true); // Temporarily allow all access
    /* ORIGINAL LOGIC - Uncomment to restore:
    // Don't run permission check until subscriptionDetails is loaded
    if (subscriptionDetails === undefined || subscriptionDetails === null) {
      return; // Wait for subscription data to load - keep hasPermission as true (default)
    }

    // If user has upgraded plan (is_locked = 0), allow all videos
    const isLocked = subscriptionDetails?.subscription?.is_locked;
    if (isLocked === '0' || isLocked === 0) {
      setHasPermission(true);
      return;
    }

    // Otherwise, check if video is in allowed list
    const allowedVideoIds = JSON.parse(localStorage.getItem('allowedVideoIds') || '[]');
    const isAllowed = allowedVideoIds.includes(String(videoID));
    setHasPermission(isAllowed);

    if (!isAllowed) {
      setShowPermissionModal(true);
    }
    */
  }, [videoID, subscriptionDetails]);

  const checkUrl = async (url) => {
    try {
      if (url !== '' && url !== null) {
        const res = await fetch(url);
        return res?.status === 200 ? url : null;
      }
      return null;
    } catch (error) {
      return null;
    }
  };
  // Apply a getVideosDetail response to player state: validate URLs, pick the
  // primary playable URL, and resolve the next video. Shared by the SWR
  // success path and the prefetch-on-navigation path so they can't diverge.
  const applyVideoDetails = (data) => {
    const newData = { ...data };
    if (data.video_file) checkUrl(data.video_file).then((result) => { newData.video_file = result; });
    if (data.video_raw_file) checkUrl(data.video_raw_file).then((result) => { newData.video_raw_file = result; });
    if (data.mp4_url) checkUrl(data.mp4_url).then((result) => { newData.mp4_url = result; });
    setVideoDetails(newData);
    const primaryUrl = newData?.transcoded_url || newData?.video_file || newData?.mp4_url || newData?.video_raw_file;
    setCurrentVideoUrl(primaryUrl);
    if (newData?.videos?.length) {
      const nextVideo = newData?.videos?.find((element) => element.order_number > data.order_number);
      setNextVideoDetails(nextVideo || newData?.videos?.[0]);
    }
  };

  // Holds the next video's full details (incl. playable URL) fetched ahead of
  // time during the end-of-video countdown, keyed by its id. Lets navigation
  // start playback instantly instead of waiting on a fresh getVideosDetail
  // round-trip — that round-trip is what made the countdown sit at "0" for a
  // couple of seconds before the next video appeared.
  const prefetchedNextRef = useRef({ id: null, data: null });
  const nextPrefetchAbortRef = useRef(null);

  // Pass-through `from` from navigation state (e.g. 'category', 'playlist') so
  // getVideosDetail receives the same context the video-details page does.
  const { from } = location.state || {};
  const { mutate: getVideoDetails } = useSWR(
    [endpoints.getVideosDetail, videoID, from],
    {
      fetcher: (url, id, fromParam) => dataFetcher(url, {
        id,
        ...(fromParam && { from: fromParam }),
      }),
      onSuccess: ({ success, data }) => {
        if (success) {
          applyVideoDetails(data);
        } else {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Oops! Something went wrong',
          });
        }

        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong',
        });
      },
    }
  );
  const { mutate: getVttContent } = useSWR(
    [endpoints.getVttContent, videoID],
    {
      fetcher: (url, video_id) => dataFetcher(url, { video_id }),
      onSuccess: ({ success, data, rows, columns, thumbnail_hover }) => {
        if (success) {
          setVttContent(data);
          setSpriteImageRow(rows);
          setSpriteImageColumn(columns);
          setSpriteImageUrl(thumbnail_hover);
        } else {
          setSpriteImageUrl(null);
        }
        setLoading(false);
      },
      onError: () => {
        navigate(Pathname.access);
        setSpriteImageUrl(null);
        setLoading(false);
      },
    }
  );
  const [state, setState] = useState({
    playing: true,
    muted: false,
    volume: parseFloat(localStorage.getItem('volume')) || 0.5,
    playbackRate: 1.0,
    played: 0,
    seeking: false,
    buffering: true
  });

  useEffect(() => {
    // If this video was prefetched during the previous countdown, apply it
    // synchronously so the player starts loading immediately (its bytes are
    // already warm) instead of waiting for the getVideosDetail round-trip.
    if (String(prefetchedNextRef.current.id) === String(videoID) && prefetchedNextRef.current.data) {
      applyVideoDetails(prefetchedNextRef.current.data);
      setLoading(false);
      prefetchedNextRef.current = { id: null, data: null };
    }
    getVideoDetails();
    getVttContent();
  }, [location.pathname]);

  // Set up 10 second timeout for video loading
  useEffect(() => {
    if (currentVideoUrl && !hasTriedFallback) {
      // Clear any existing timeout
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }

      // Set timeout - longer for HLS streams (10s) vs regular videos (6s)
      const isHlsStream = currentVideoUrl?.includes('.m3u8');
      const timeoutDuration = isHlsStream ? 10000 : 6000;
      // eslint-disable-next-line no-console
      // console.log(`⏱️ [DEBUG] Starting ${timeoutDuration / 1000} second timeout for ${isHlsStream ? 'HLS' : 'regular'} URL:`, currentVideoUrl);
      loadTimeoutRef.current = setTimeout(() => {
        // eslint-disable-next-line no-console
        // console.log('⏱️ [DEBUG] Timeout fired! hasTriedFallback:', hasTriedFallback, 'video_raw_file:', videoDetails?.video_raw_file);
        // If video hasn't started after timeout, try fallback if available
        if (!hasTriedFallback) {
          if (videoDetails?.video_raw_file) {
            const fallbackUrl = videoDetails.video_raw_file;
            // eslint-disable-next-line no-console
            // console.log('🔄 [DEBUG] TIMEOUT FALLBACK - Switching to video_raw_file:', fallbackUrl);
            logAppEvent({
              userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
              functionAffected: 'Video Playback',
              description: `Primary URL timed out — switching to fallback. Primary URL: ${currentVideoUrl}, Fallback URL: ${fallbackUrl}`,
              severity: 'medium',
              errorMessage: 'Primary video URL timed out, fallback attempted',
              requestUrl: currentVideoUrl,
              component: 'VideoPlayer',
              userAction: 'Video playback called',
            });
            setCurrentVideoUrl(fallbackUrl);
            setHasTriedFallback(true);
          } else {
            // eslint-disable-next-line no-console
            // console.warn('⚠️ [DEBUG] No fallback URL available - video is taking too long to load');
            logAppEvent({
              userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
              functionAffected: 'Video Playback',
              description: `Video failed to load (timeout). URL: ${currentVideoUrl}`,
              severity: 'high',
              errorMessage: 'Video failed to load within timeout',
              requestUrl: currentVideoUrl,
              component: 'VideoPlayer',
              userAction: 'Video playback called',
            });
          }
        }
      }, timeoutDuration);
    }

    // Cleanup timeout on unmount
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [currentVideoUrl, hasTriedFallback, videoDetails, setAppSnackbar]);

  // Timeout for fallback URL — if fallback also stalls with no error, loader spins forever
  const fallbackTimeoutRef = useRef(null);
  useEffect(() => {
    if (!hasTriedFallback || !currentVideoUrl) return undefined;

    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
    }

    const isHlsStream = currentVideoUrl?.includes('.m3u8');
    const timeoutDuration = isHlsStream ? 15000 : 10000;

    fallbackTimeoutRef.current = setTimeout(() => {
      logAppEvent({
        userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Video Playback',
        description: `Fallback URL also failed to load (timeout) — video loader stuck. Fallback URL: ${currentVideoUrl}`,
        severity: 'high',
        errorMessage: 'Both primary and fallback URLs failed to load — infinite loader',
        requestUrl: currentVideoUrl,
        component: 'VideoPlayer',
        userAction: 'Video playback called',
      });
    }, timeoutDuration);

    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, [hasTriedFallback, currentVideoUrl]);

  const classes = useStyles();
  const { playing, muted, volume, playbackRate, played, seeking, buffering, loaded = 0 } = state;

  // Synthetic progress signal for the loader ring. Just using state.loaded*100
  // looks broken because state.loaded is the fraction of the whole file buffered —
  // often 0 during the moment buffering starts. Instead we bump at lifecycle
  // milestones (metadata = 50, buffering-end = 85, playback resumed = 100) and
  // additionally track loaded while buffering, so the ring is always moving.
  const [loadPercent, setLoadPercent] = useState(0);
  // Hold the loader open for ~400 ms after first playback so the ring's
  // animation to 100 is actually visible — for cached/fast-start videos the
  // metadata→canplay→playing burst can finish in <50 ms otherwise.
  const finalizingRef = useRef(false);
  // Where the playhead was when we entered the current buffering state. Lets
  // us tell "first load" (null) from "mid-play rebuffer" (number) and only
  // finalize when the player has actually advanced past the stall point.
  const stalledAtPlayedSecondsRef = useRef(null);
  // Latest buffering value, readable from non-React callbacks (handleBufferStart
  // fires from a player event and needs to know the current state synchronously).
  const bufferingRef = useRef(buffering);
  useEffect(() => { bufferingRef.current = buffering; }, [buffering]);

  // Reset on URL change — but only on a real switch (e.g. fallback / next video),
  // never on the initial null→URL transition. A reset during the initial set
  // yanks the fake-climb percent back to 0 right when the API resolves,
  // producing the visible "goes back then forward" glitch.
  const prevUrlRef = useRef(null);
  useEffect(() => {
    if (prevUrlRef.current !== null && prevUrlRef.current !== currentVideoUrl) {
      setLoadPercent(0);
      finalizingRef.current = false;
      stalledAtPlayedSecondsRef.current = null;
    }
    prevUrlRef.current = currentVideoUrl;
  }, [currentVideoUrl]);

  // While buffering, track real loaded bytes (clamped to 99 so we don't reach
  // 100 before playback actually resumes).
  useEffect(() => {
    if (!buffering) return;
    const pct = Math.min(99, Math.max(0, loaded * 100));
    setLoadPercent((prev) => Math.max(prev, pct));
  }, [buffering, loaded]);

  // Asymptotic fake climb to 90% during buffering. Hides the dead time when
  // preload="metadata" leaves video.buffered empty and real signals are silent.
  // Real progress (loaded*100, milestone bumps to 50/85, final snap to 100) all
  // still win via Math.max — this only fills the floor, never the ceiling.
  // Gated on currentVideoUrl so we don't ramp during the API-loading phase
  // (otherwise URL-set would visibly reset the climb).
  useEffect(() => {
    if (!buffering || !currentVideoUrl) return undefined;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsedSec = (Date.now() - start) / 1000;
      const target = 90 * (1 - Math.exp(-elapsedSec / 2.5));
      setLoadPercent((prev) => Math.max(prev, target));
    }, 150);
    return () => clearInterval(id);
  }, [buffering, currentVideoUrl]);

  const playerContainerRef = useRef(null);
  const controlsRef = useRef(null);

  // Force the controls wrapper visible whenever we're buffering — the loader
  // ring lives inside that wrapper, so hiding it on idle hides the ring too.
  useEffect(() => {
    if (buffering && controlsRef.current) {
      controlsRef.current.style.visibility = 'visible';
    }
  }, [buffering]);

  // Fires when the player itself signals a stall (NativeVideoPlayer debounces
  // the underlying 'waiting' event by 400 ms, so by the time this runs it's
  // a real rebuffer, not a micro-stall). Records where we stalled so we know
  // when the player has truly recovered.
  const handleBufferStart = () => {
    if (bufferingRef.current) return; // already showing the loader
    const current = playerRef.current?.getCurrentTime?.();
    stalledAtPlayedSecondsRef.current = typeof current === 'number' ? current : null;
    finalizingRef.current = false;
    // Don't reset loadPercent here — a mid-load stall that races the finalize
    // timer would otherwise yank the ring from 100 back to 0 and re-climb,
    // producing the visible "goes back then forward again" glitch.
    setState((prev) => ({ ...prev, buffering: true }));
  };

  // Handle video error - fallback to video_raw_file or pause for autoplay
  const handleVideoError = (error) => {
    // eslint-disable-next-line no-console
    // console.log('❌ [DEBUG] handleVideoError called:', error);
    // Check if it's an autoplay error - pause video instead
    if (error?.name === 'NotAllowedError' || (error?.message && error.message.includes('interact'))) {
      // eslint-disable-next-line no-console
      // console.log('❌ Autoplay error - pausing video');
      logAppEvent({
        userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Video Playback',
        description: `Autoplay blocked by browser — video paused. URL: ${currentVideoUrl}`,
        severity: 'low',
        errorMessage: error?.message || 'NotAllowedError — autoplay blocked',
        requestUrl: currentVideoUrl,
        component: 'VideoPlayer',
        userAction: 'Video playback called',
      });
      setState((prev) => ({ ...prev, playing: false, buffering: false }));
      return;
    }

    // Clear timeout if it exists
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    // Try fallback URL if not already tried
    if (!hasTriedFallback && videoDetails?.video_raw_file) {
      const fallbackUrl = videoDetails.video_raw_file;
      // eslint-disable-next-line no-console
      // console.log('❌ [DEBUG] ERROR FALLBACK - Switching to video_raw_file:', fallbackUrl);
      logAppEvent({
        userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Video Playback',
        description: `Primary URL failed — switching to fallback. Primary URL: ${currentVideoUrl}, Fallback URL: ${fallbackUrl}`,
        severity: 'medium',
        errorMessage: error?.message || 'Primary video URL failed, fallback attempted',
        requestUrl: currentVideoUrl,
        component: 'VideoPlayer',
        userAction: 'Video playback called',
      });
      setCurrentVideoUrl(fallbackUrl);
      setHasTriedFallback(true);
    } else {
      logAppEvent({
        userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Video Playback',
        description: `Video failed to load. URL: ${currentVideoUrl}`,
        severity: 'high',
        errorMessage: error?.message || 'Video failed to load',
        requestUrl: currentVideoUrl,
        component: 'VideoPlayer',
        userAction: 'Video playback called',
      });
    }
  };

  // Handle video ready (metadata/manifest loaded - video may not be playing yet)
  const handleVideoReady = () => {
    // eslint-disable-next-line no-console
    // console.log('✅ handleVideoReady called - metadata loaded');
    // Don't clear timeout or hide loader here - wait until video actually starts playing
    // Metadata is in — bump the loader ring so it's not stuck at low %.
    setLoadPercent((prev) => Math.max(prev, 50));
  };

  // Handle when video actually starts playing
  const handleVideoPlay = () => {
    // eslint-disable-next-line no-console
    // console.log('▶️ [DEBUG] VIDEO STARTED PLAYING!', {
    //   currentVideoUrl,
    //   player: currentVideoUrl?.includes('.m3u8') ? 'HlsPlayer' : 'ReactPlayer',
    //   hasTriedFallback,
    // });
    // Clear the fallback timeout since video is starting
    if (loadTimeoutRef.current) {
      // eslint-disable-next-line no-console
      // console.log('▶️ [DEBUG] Clearing timeout - video is playing');
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    // Don't hide loader here - let handleProgress do it when video has actual progress
    // This ensures loader stays until video frames are actually rendering

    // Visible player owns the socket now — stop racing it for warmup bytes
    // and tear down the hidden warm-up <video> below.
    if (prefetchAbortRef.current) {
      prefetchAbortRef.current.abort();
      prefetchAbortRef.current = null;
    }
    setHasStartedPlayback(true);
    // Don't snap loadPercent to 100 here — let handleProgress finalize it
    // when playedSeconds actually advances. Snapping here causes a visible
    // jump from the 85% set by onEndBuffering straight to 100, skipping the
    // smooth animation the CSS transition is supposed to provide.

    window.gtag?.('event', 'video_play', {
      video_title: videoDetails?.title,
      video_id: videoDetails?.id,
    });
  };

  // state changing codes.....
  const handlePlayPause = () => {
    setState({ ...state, playing: !state.playing });
  };
  const handleRewind = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() - 15);
  };
  const handleFastForward = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + 15);
  };

  const handleUserKeyPress = (event) => {
    const { key, keyCode } = event;

    if (keyCode === 32) {
      handlePlayPause();
    }
    if (keyCode === 37) { // left arrow key
      handleRewind();
    }
    if (keyCode === 39) { // right arrow key
      handleFastForward();
    }
  };
  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  });
  // show or hide controllers after 3 sec of last mouse move
  const showOrHideControllers = () => {
    if (controlsRef.current && controlsRef.current.style.visibility !== 'visible') {
      controlsRef.current && (controlsRef.current.style.visibility = 'visible');
    }

    if (showPlayAgainButton && !buffering) {
      clearTimeout(timeoutRef.current);
    } else {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (controlsRef.current && controlsRef.current.style.visibility !== 'hidden') {
          controlsRef.current && (controlsRef.current.style.visibility = 'hidden');
          setHoverValue(null);
        }
      }, 2000);
    }
  };

  useEffect(() => {
    // Hide icon after 2 sec after start the video play (without mouse move)
    if (!showPlayAgainButton && !buffering) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (controlsRef.current && controlsRef.current.style.visibility !== 'hidden') {
          controlsRef.current.style.visibility = 'hidden';
          setHoverValue(null);
        }
      }, 2000);

      // Add event listeners only if buffering is false (include touch events for mobile Safari)
      ['mousemove', 'click', 'touchstart'].forEach((evt) => document.addEventListener(evt, showOrHideControllers, false));

      // Cleanup function to remove event listeners AND cancel the pending
      // hide-timer. Without the clearTimeout, a hide scheduled here keeps
      // firing after showPlayAgainButton flips true (video completed), which
      // hid the controls + next-video overlay that should stay on screen.
      return () => {
        clearTimeout(timeoutRef.current);
        ['mousemove', 'click', 'touchstart'].forEach((evt) => document.removeEventListener(evt, showOrHideControllers, false));
      };
    }
    return undefined;
  }, [showPlayAgainButton, playing, buffering]);

  const handleMute = () => {
    setState({ ...state, muted: !state.muted });
  };
  const handleVolumeChange = (newValue) => {
    newValue === 0 ? setState({
      ...state,
      volume: Math.floor(newValue),
      muted: true,
    })
      : setState({
        ...state,
        volume: Math.floor(newValue),
        muted: false,
      });
  };
  const handleVolumeSeekDown = (newValue) => {
    newValue < 0.027 ? setState({
      ...state,
      volume: 0, // Volume should be 0
      muted: true, // Mute should be true
    })
      : setState({
        ...state,
        volume: newValue,
        muted: false,
      });
  };
  const handelPlaybackRate = (value) => {
    setState({
      ...state,
      playbackRate: value,
    });
  };
  const toggleFullScreen = () => {
    screenfull.toggle(playerContainerRef.current);
    setScreenSize(!screenSize);
  };
  const currentTime = playerRef.current
    ? playerRef.current.getCurrentTime()
    : '00:00';
  const duration = playerRef.current
    ? playerRef.current.getDuration()
    : '00:00';
  const elapsedTime = format(currentTime);
  const totalDuration = format(duration);

  const { mutate: updateVideoPaused } = useSWR(
    [endpoints.updateVideoPaused, videoID, localStorage.getItem('video_duration') || elapsedTime],
    {
      fetcher: (url, video_id, paused_at) => dataFetcher(url, { video_id, paused_at }),
      onSuccess: ({ success, data }) => {
        // if (success) {
        // } else {
        // }
      },
      onError: () => {
      },
    }
  );

  const handleProgress = (changeState) => {
    if (!seeking) {
      // Did the player actually advance past the stall point? For first load
      // (no stallpoint), any progress counts. For mid-play rebuffer, require
      // 0.5s past where we stalled — guards against the player briefly
      // reporting one or two frames advance during a continuing stall.
      const stalledAt = stalledAtPlayedSecondsRef.current;
      // Cap the resume target just shy of the clip's end. Seeking/stalling near
      // the end would otherwise need a position past the video's duration (it
      // ends first), so buffering never clears and the loader sticks at 100%.
      const dur = playerRef.current?.getDuration?.() || 0;
      const resumeTarget = dur > 0
        ? Math.min(stalledAt + 0.5, dur - 0.25)
        : stalledAt + 0.5;
      const hasAdvanced = stalledAt === null
        ? changeState.playedSeconds > 0
        : changeState.playedSeconds > resumeTarget;

      if (hasAdvanced && buffering && !finalizingRef.current) {
        finalizingRef.current = true;
        setLoadPercent(100);
        setTimeout(() => {
          setState((prev) => ({ ...prev, buffering: false }));
          stalledAtPlayedSecondsRef.current = null;
          finalizingRef.current = false;
        }, 400);
      }
      setState((prev) => ({ ...prev, ...changeState }));
    }
    // if (playing) {
    //   updateVideoPaused();
    // }
  };

  useEffect(() => {
    let count = 0;
    if (localStorage?.getItem('video_id') && localStorage?.getItem('video_duration')) {
      window.addEventListener('popstate', async () => {
        if (!count) {
          await axios
            .post(endpoints.updateVideoPaused, { video_id: localStorage.getItem('video_id'), paused_at: localStorage.getItem('video_duration') })
            .then(({ data }) => {
              localStorage.setItem('video_id', null);
              localStorage.setItem('video_duration', null);
            })
            .catch((error) => {
              localStorage.setItem('video_id', null);
              localStorage.setItem('video_duration', null);
            });
          count += 1;
        }
      });
    }
  }, []);
  const elapsedTimeNumber = parseFloat(elapsedTime);
  const apiUrl = `${baseUrl}/updateViewersDetails`;
  const video_id = videoID;
  const token = getToken();
  const [draftId] = useState(uuid());
  const formatTime = (time) => {
    const seconds = Math.floor(time);
    const milliseconds = Math.floor((time - seconds) * 100);
    return `${seconds}.${milliseconds}`;
  };
  const postData = {
    video_id,
    token,
    'uuid': draftId,
    'current': currentTime
  };

  const makeApiCall = async () => {
    if (playing) {
      try {
        const response = await axios.post(apiUrl, postData);
      } catch (error) {
        // Error handling
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(makeApiCall, 10000);
    return () => clearInterval(interval);
  }, [playing]);

  useEffect(() => {
    if (elapsedTime && playing) {
      localStorage.setItem('video_duration', elapsedTime);
      localStorage.setItem('video_id', videoID);
    }
  }, [elapsedTime]);

  const handleSeekChange = (newValue) => {
    setState({ ...state, played: newValue });
  };
  const handleSeekMouseDown = (e) => {
    setState({ ...state, seeking: true });
  };
  const handleSeekMouseUp = (newValue) => {
    setState({ ...state, seeking: false });
    playerRef.current.seekTo(newValue);
  };

  const onEndBuffering = () => {
    // eslint-disable-next-line no-console
    // console.log('🔄 onEndBuffering called - buffering ended');
    // Clear the fallback timeout but don't hide loader yet - wait for handleVideoPlay
    if (loadTimeoutRef.current) {
      // eslint-disable-next-line no-console
      // console.log('🔄 Clearing timeout in onEndBuffering');
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    // Buffer fill resolved — almost ready; bump the ring before playback resumes.
    setLoadPercent((prev) => Math.max(prev, 85));
    // Don't set buffering: false here - let handleVideoPlay do it when video actually plays
  };

  const onStarted = () => {
    type === 'resumeWatching' && playerRef.current.seekTo(playerRef.current.getCurrentTime() + videoDetails.paused_at);
  };

  // Fired by the <video> 'ended' event. This is the reliable end-of-video
  // signal: it covers both natural completion AND seeking to the very end,
  // where the browser clamps currentTime just under duration so the
  // currentTime === duration check below never matches and the play-again /
  // next-video overlay would otherwise never appear.
  const handleVideoEnded = () => {
    setPlayAgainButton(true);
  };

  useEffect(() => {
    if ((currentTime && duration) && currentTime !== '00:00' && currentTime === duration) {
      setTimeout(() => {
        // navigate(-2);
        setPlayAgainButton(true);
      }, 500);
    }
  }, [currentTime]);

  useEffect(() => {
    if (!showPlayAgainButton) return undefined;
    // The instant the countdown reaches 0, go to the next video. Its details
    // and bytes were prefetched during the countdown (see effect below), so
    // playback starts immediately instead of sitting on "0s" while a fresh
    // getVideosDetail round-trip happens.
    if (countDown <= 0) {
      navigate(
        nextVideoDetails?.id
          ? Pathname.getPlayerPath(nextVideoDetails.id, 'play')
          : Pathname.getVideoPath(videoID),
        from ? { state: { from } } : undefined
      );
      return undefined;
    }

    // save intervalId to clear the interval when the
    // component re-renders
    const intervalId = setInterval(() => {
      setCountDown(countDown - 1);
    }, 1000);
    // clear interval on re-render to avoid memory leaks
    return () => clearInterval(intervalId);
  }, [countDown, showPlayAgainButton, location.pathname]);

  // Prefetch the next video's full details + warm its first bytes while the
  // end-of-video countdown is running. Without this, navigating to the next
  // video kicks off the getVideosDetail fetch + buffering only at that moment,
  // which left the countdown frozen at "0" for ~2s before the video appeared.
  useEffect(() => {
    if (!showPlayAgainButton) return undefined;
    const nextId = nextVideoDetails?.id;
    if (!nextId) return undefined;
    if (String(prefetchedNextRef.current.id) === String(nextId) && prefetchedNextRef.current.data) {
      return undefined;
    }

    const ctrl = new AbortController();
    nextPrefetchAbortRef.current = ctrl;
    (async () => {
      try {
        const res = await dataFetcher(endpoints.getVideosDetail, { id: nextId, ...(from && { from }) });
        if (ctrl.signal.aborted || !res?.success || !res?.data) return;
        prefetchedNextRef.current = { id: nextId, data: res.data };
        const d = res.data;
        const url = d?.transcoded_url || d?.video_file || d?.mp4_url || d?.video_raw_file;
        if (url && !url.includes('.m3u8')) {
          const p = await probeVideo(url, ctrl.signal);
          if (!ctrl.signal.aborted && p?.available && p?.acceptRanges) {
            // Fire-and-forget byte warmup into the browser HTTP cache.
            prefetch(url, p, ctrl.signal);
          }
        }
      } catch (e) {
        // Best-effort only — navigation falls back to a normal fetch.
      }
    })();
    return () => ctrl.abort();
  }, [showPlayAgainButton, nextVideoDetails?.id, from]);

  useEffect(() => {
    // Don't re-arm the counter while the completed-video overlay is still up.
    // When the "Play Next Video in: Xs" countdown hits 0 it navigates to the
    // next video (changing pathname); resetting to 10 here would restart the
    // countdown and make it loop 10→0→10 until the next video loads. Instead
    // keep it pinned at 0 — it re-arms only once the new video starts and the
    // overlay is dismissed (setPlayAgainButton(false)).
    if (showPlayAgainButton) return undefined;
    const id = setTimeout(() => {
      setCountDown(10);
    }, 1000);
    return () => clearTimeout(id);
  }, [location.pathname, showPlayAgainButton]);

  useEffect(() => {
    if (showPlayAgainButton) {
      // Cancel any pending auto-hide so the completed-video overlay (controls +
      // next-video countdown) can't be hidden out from under us.
      clearTimeout(timeoutRef.current);
      if (controlsRef.current && controlsRef.current.style.visibility !== 'visible') {
        controlsRef.current && (controlsRef.current.style.visibility = 'visible');
      }
    }
  }, [showPlayAgainButton]);

  // const onEnded = () => {
  // setPlayAgainButton(true);
  // setState({ ...state, playing: false });
  //   setTimeout(() => { navigate(-1); }, 2000);
  // };

  useEffect(() => {
    // Play Again: when the completed-video overlay is dismissed, restart from
    // the beginning. Gate on the LIVE player position being at the very end
    // (not an exact currentTime === duration float compare, which
    // intermittently failed after a previous replay and left the video frozen /
    // black; and not the stale `played` state, which would misfire when the
    // overlay is dismissed because a *new* video just began). Then force the
    // underlying <video> to play directly — at the end of a clip `playing` is
    // already true, so setState alone wouldn't re-trigger the play/pause effect
    // and playback would never resume (loader stuck at 100%).
    if (showPlayAgainButton) return;
    const player = playerRef.current;
    if (!player) return;
    const cur = player.getCurrentTime?.() || 0;
    const dur = player.getDuration?.() || 0;
    if (dur > 0 && cur >= dur - 0.5) {
      player.seekTo(0);
      const internal = player.getInternalPlayer?.();
      if (internal) {
        try { internal.currentTime = 0; } catch (e) { /* ignore */ }
        const playPromise = internal.play?.();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      }
      setState((prev) => ({ ...prev, playing: true, played: 0 }));
      setCountDown(10);
    }
  }, [showPlayAgainButton]);

  useEffect(() => {
    localStorage.setItem('volume', volume);
  }, [volume]);

  useEffect(() => {
    if (currentTime === 0) {
      setPlayAgainButton(false);
    }
  }, [currentTime]);

  return (
    <>
      <div>
        <div ref={playerContainerRef} className={classes.playerWrapper}>
          <div className={styles.videoContainer} style={{ filter: !hasPermission ? 'blur(20px)' : 'none' }}>
            {/* Use custom HLS player for m3u8 URLs to fix audio issues */}
            {currentVideoUrl?.includes('.m3u8') ? (
              <HlsPlayer
                key={currentVideoUrl}
                ref={playerRef}
                width="100%"
                height="100%"
                className={styles.react_player}
                style={{ width: '100%', height: '100%', objectFit: videoObjectFit }}
                url={hasPermission ? currentVideoUrl : null}
                muted={muted}
                playing={hasPermission && playing}
                volume={volume}
                playbackRate={playbackRate}
                onProgress={handleProgress}
                onBuffer={handleBufferStart}
                onBufferEnd={onEndBuffering}
                onReady={handleVideoReady}
                onStart={onStarted}
                onError={handleVideoError}
                onPlay={handleVideoPlay}
                onEnded={handleVideoEnded}
              />
            ) : (
              <>
                <NativeVideoPlayer
                  key={currentVideoUrl}
                  ref={playerRef}
                  width="100%"
                  height="100%"
                  className={styles.react_player}
                  style={{ width: '100%', height: '100%', objectFit: videoObjectFit }}
                  url={hasPermission ? currentVideoUrl : null}
                  probe={probe}
                  muted={muted}
                  playing={hasPermission && playing}
                  volume={volume}
                  playbackRate={playbackRate}
                  onProgress={handleProgress}
                  onBuffer={handleBufferStart}
                  onBufferEnd={onEndBuffering}
                  onReady={handleVideoReady}
                  onStart={onStarted}
                  onError={handleVideoError}
                  autoPlay={hasPermission}
                  onPlay={handleVideoPlay}
                  onEnded={handleVideoEnded}
                  progressInterval={100}
                />
                {/* Hidden warm-up <video>. The browser fetches bytes for this
                    URL into the media cache; when the visible NativeVideoPlayer
                    issues range requests they hit the warm cache. Restricted
                    to mp4/webm — warming a TS file mpegts.js will re-fetch via
                    JS is wasted bandwidth, and unsupported formats won't play
                    at all. */}
                {(() => {
                  if (!currentVideoUrl || hasStartedPlayback) return null;
                  const cls = classifyFormat(currentVideoUrl, probe);
                  if (cls.kind !== 'mp4' && cls.kind !== 'webm') return null;
                  return (
                    <video
                      key={`warmup-${currentVideoUrl}`}
                      src={currentVideoUrl}
                      preload="auto"
                      muted
                      playsInline
                      aria-hidden="true"
                      tabIndex={-1}
                      style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        opacity: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })()}
              </>
            )}
          </div>
          {/* style={{ display: toolsVesibility ? 'block' : 'none' }} */}
          {hasPermission && (
            <div>
              <PlayerControls
                ref={controlsRef}
                onPlayPause={handlePlayPause}
                playing={playing}
                onRewind={handleRewind}
                onFastForward={handleFastForward}
                muted={muted}
                onMute={handleMute}
                volume={volume}
                onVolumeChange={handleVolumeChange}
                onVolumeSeekUp={handleVolumeSeekDown}
                playbackRate={playbackRate}
                onPlaybackRateChange={handelPlaybackRate}
                onToggleFullScreen={toggleFullScreen}
                played={played}
                onSeek={handleSeekChange}
                onSeekMouseDown={handleSeekMouseDown}
                onSeekMouseUp={handleSeekMouseUp}
                currentTime={currentTime}
                elapsedTime={elapsedTime}
                totalDuration={totalDuration}
                title={videoDetails.title}
                videoID={videoID}
                buffering={buffering}
                loadPercent={loadPercent}
                loaded={loaded}
                vttContent={vttContent}
                duration={duration}
                rows={spriteImageRow}
                columns={spriteImageColumn}
                spriteImage={spriteImageUrl}
                screenSize={screenSize}
                format={format}
                showPlayAgainButton={showPlayAgainButton}
                setPlayAgainButton={setPlayAgainButton}
                showControllers={showControllers}
                setControllers={setControllers}
                hoverValue={hoverValue}
                setHoverValue={setHoverValue}
                setState={setState}
                state={state}
                nextVideoDetails={nextVideoDetails}
                countDown={countDown}
                from={from}
              />
            </div>
          )}
        </div>
      </div>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
      />
    </>
  );
}
